import useCommonGenerateReport from '@/core/domain/reports/commons/Report.hook';
import { useReportTemplateGenerateReceipt } from '@/core/domain/reports/templates/ReportTemplates.hooks';
import {
  useDefaultEmptyFieldError,
  useDisclosure,
  useReportGenerate,
} from '@/hooks';
import { Button, Checkbox, Form, Modal, Popover, Space } from 'antd';
import React, { useEffect } from 'react';
import FloatingFormItem from '../FloatingFormItem';
import EmployeesInfiniteSelectField from '../EmployeesInfiniteSelectField';
import CompaniesInfiniteSelectField from '../CompaniesInfiniteSelectField';
import { Destination } from '@/core/domain/financial/BankingEntry/BankingEntry.types';

interface Props {
  children?: React.ReactNode | undefined;
  bankEntryId: number;
  employeeId?: number;
  companyId?: number;
  generateDirectly?: boolean;
  nature?: Destination;
}

export default function EmployeePopoverGenerateReceipt(props: Props) {
  const [formEmployee] = Form.useForm<{
    employeeId?: number;
    showEmployeeDocument: boolean;
    useCompanyName: boolean;
  }>();
  const [formCompany] = Form.useForm<{
    companyId?: number;
    showEmployeeDocument: boolean;
  }>();
  const requiredField = useDefaultEmptyFieldError();

  const [selectEmplooyeeModal, selectEmployeeModalActions] = useDisclosure();
  const [selectCompanyModal, selectCompanyModalActions] = useDisclosure();

  const useCompanyName = Form.useWatch('useCompanyName', formEmployee);

  const report = useReportGenerate();
  const generateReceiptReport = useReportTemplateGenerateReceipt();
  const generateBankReleaseReceipt = useCommonGenerateReport(
    '/generate/bank-release-receipt',
    'Recibo gerado com sucesso!',
    'Recibo de lançamento bancário gerado com sucesso.',
  );

  const handleClose = () => {
    selectEmployeeModalActions.close();
    selectCompanyModalActions.close();
    formEmployee.resetFields();
  };

  const handleEmployeeOk = async () => {
    const { employeeId, showEmployeeDocument, useCompanyName } =
      await formEmployee.validateFields();

    const result = await generateReceiptReport.mutateAsync({
      bankEntryId: props.bankEntryId,
      employeeId,
      showEmployeeDocument,
      useCompanyName,
    });

    report.openFile({
      result,
      fileName: 'recibo',
      fileType: 'application/pdf',
    });

    handleClose();
  };

  const handleCompanyOk = async () => {
    const value = await formCompany.validateFields();

    const companyId = props.generateDirectly
      ? props.companyId
      : value.companyId;

    window.open(`/privateReports/receiptRecord/${companyId}`, '_blank');

    handleClose();
  };

  useEffect(() => {
    if (props?.employeeId) {
      formEmployee.setFieldValue('employeeId', props.employeeId);
    }
  }, [selectEmplooyeeModal, props?.employeeId]);

  useEffect(() => {
    if (props?.companyId) {
      formCompany.setFieldValue('companyId', props.companyId);
    }
  }, [selectCompanyModal, props?.companyId]);

  return (
    <>
      <Popover
        title="Gerar"
        trigger="click"
        content={
          <Space direction="vertical">
            <Button
              block
              size="small"
              loading={generateBankReleaseReceipt.isLoading}
              onClick={async () => {
                const result = await generateBankReleaseReceipt.mutateAsync({
                  bankEntrieId: props.bankEntryId,
                });

                report.openFile({
                  result,
                  fileName: 'recibo',
                  fileType: 'application/pdf',
                });
              }}
            >
              Recibo da oficina
            </Button>

            <Button
              block
              size="small"
              loading={
                generateReceiptReport.isLoading && !props.generateDirectly
              }
              onClick={selectEmployeeModalActions.open}
            >
              Recibo em duas vias
            </Button>

            <Button
              block
              size="small"
              loading={
                generateReceiptReport.isLoading && props.generateDirectly
              }
              onClick={() => {
                if (props.generateDirectly) {
                  return handleCompanyOk();
                }
                selectCompanyModalActions.open();
              }}
            >
              Recibo em branco
            </Button>
          </Space>
        }
      >
        {props.children}
      </Popover>

      <Modal
        zIndex={2000}
        title={`Funcionário que ${
          props.nature === Destination.revenue ? 'recebeu' : 'efetuou'
        } o Pagamento`}
        okText="Gerar Recibo"
        open={selectEmplooyeeModal}
        onCancel={handleClose}
        onOk={handleEmployeeOk}
        confirmLoading={generateReceiptReport.isLoading}
      >
        <Form form={formEmployee}>
          {!useCompanyName && (
            <>
              <FloatingFormItem
                name="employeeId"
                label="Funcionário"
                rules={[requiredField]}
              >
                <EmployeesInfiniteSelectField
                  dropdownStyle={{ zIndex: 2001 }}
                />
              </FloatingFormItem>

              <FloatingFormItem
                name="showEmployeeDocument"
                valuePropName="checked"
              >
                <Checkbox>Exibir CPF do funcionário</Checkbox>
              </FloatingFormItem>
            </>
          )}

          <FloatingFormItem name="useCompanyName" valuePropName="checked">
            <Checkbox>Usar o nome da empresa</Checkbox>
          </FloatingFormItem>
        </Form>
      </Modal>

      <Modal
        title="Empresa que recebeu o Pagamento"
        okText="Gerar Recibo"
        open={selectCompanyModal}
        onCancel={handleClose}
        onOk={handleCompanyOk}
        confirmLoading={generateReceiptReport.isLoading}
      >
        <Form form={formCompany}>
          <FloatingFormItem
            name="companyId"
            label="Empresa"
            rules={[requiredField]}
          >
            <CompaniesInfiniteSelectField />
          </FloatingFormItem>
        </Form>
      </Modal>
    </>
  );
}
